/* eslint-disable @typescript-eslint/no-explicit-any */
import styles from './ccf-interaction-space-header-tab.module.scss';
import { useEffect, useState } from 'react';
import { DigitalChannelType, InteractionTabType } from '@nice-ccf/core-sdk';
import {
  activeInteraction,
  getCoBrowsingSessionClosed,
  getInteractionMsgCount,
  interactionSliceActions,
} from '../../ccf-interaction-workspace/ccf-interaction-space.slice';
import {
  CcfApprovedIcon,
  CcfAvailableIcon,
  CcfButton,
  CcfDoubleArrowIcon,
  CcfHeader,
  CcfHeaderProps,
  CcfInstaIcon,
  CcfLinkedinIcon,
  CcfNewWindowIcon,
  CcfPopOver,
  CcfShowOriginIcon,
  CcfSmsIcon,
  CcfChatIcon,
  CcfTranscriptIcon,
  CcfTweetEyeIcon,
  CcfTypography,
  CcfFacebookIcon,
  CcfAddChannelIcon,
  CcfChatBoxMessageCountIcon,
  CcfCoBrowseIcon,
  CcfBox,
  CcfXIcon,
} from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import { CcfTwitterIcon, CcfEmailIcon, useTranslator } from '@nice-ccf/ui-controls';
import { Menu, MenuItem } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { PopOverMenuItem } from '../../ccf-inbox/model/callControlPopover';
import {
  activeContactSelector,
  getActiveInteractionID,
  getActiveInteractionType,
  getInteractionTabs,
  updateActiveInteraction,
} from '@nice-ccf/ui-components';
import { getMessageList } from '../../ccf-interaction-tabs/ccf-chat-box/ccf-chat-box.slice';
import {
  getCoBrowsingSessionStarted,
  getContactElevationDetails,
  setCoBrowsingSessionStarted,
} from '../../ccf-inbox/ccf-inbox.slice';


const useStyles = makeStyles((theme) =>
  createStyles({
    iconButtonRoot: {
      '&.MuiIconButton-root': {
        padding: '0px',
      },
    },
    svgRoot: {
      '&.MuiSvgIcon-root': {
        fontSize: '4em',
      },
    },
    buttonRoot: {
      '&.MuiButton-root': {
        padding: '0px',
        height: '20px',
        minWidth: 'inherit',
        font: "normal normal 500 9px/30px 'Open-Sans'",
        '&.MuiButton-root:hover': {
          backgroundColor: '#E2F0F7',
        },
      },
    },
    listRoot: {
      '&.MuiList-root': {
        padding: '0px',
      },
    },
    listItemRoot: {
      '&.MuiListItem-root': {
        fontSize: 'inherit',
        '&.MuiListItem-root:hover': {
          backgroundColor: '#E2F0F7',
        },
      },
    },
  })
);

/**
 * Component displays Icon for interaction Tab
 * @param tabName -string
 * @returns Icon for interaction Tab
 * ```
 * @example
 * renderIcons('sms')
 * ```
 */
const renderIcons = (tabName: string) => {
  switch (tabName) {
    case 'tw':
      return <CcfXIcon />;
    case InteractionTabType.FACEBOOK:
      return <CcfFacebookIcon />;
    case 'ig':
    case InteractionTabType.INSTAGRAM:
      return <CcfInstaIcon />;
    case 'fb':
      return <CcfFacebookIcon />;
    case 'li':
      return <CcfLinkedinIcon />;
    case 'chat':
    case InteractionTabType.CHAT:
      return <CcfChatIcon />;
    case 'CopilotChat':
    case InteractionTabType.COPILOTCHAT:
      return <CcfChatIcon />;
    case 'sms':
    case InteractionTabType.SMS:
      return <CcfSmsIcon />;
    case 'email':
      return <CcfEmailIcon className={styles.emailIcon} />;
    default:
      return null;
  }
};

/* eslint-disable-next-line */
export interface CcfInteractionSpaceHeaderTabProps {}

/**
 * Component to displays InteractionSpace HeaderTab
 * @returns InteractionSpace HeaderTab
 * ```
 * @example
 * <CcfInteractionSpaceHeaderTab showDragIcon
      RightIcon={false}
      headerTextClassess="customerName marginLeft12"/>
 * ```
 */
export function CcfInteractionSpaceHeaderTab(props: CcfHeaderProps) {
  const [translate] = useTranslator();
  const activeInt = useSelector(activeInteraction);
  const selectedCallContact = useSelector(activeContactSelector);
  const interactionTabs = useSelector(getInteractionTabs) || {};
  const activeInteractionID = useSelector(getActiveInteractionID) || '';
  const { setInteractionHeaderTabActive } = interactionSliceActions;
  const dispatch = useDispatch();
  const [viewDetails, setViewDetails] = useState(false);
  const activeInteractionType = useSelector(getActiveInteractionType);
  const getMessageCount = useSelector(getMessageList);
  const interactionMSgCount = useSelector(getInteractionMsgCount);
  const isInteractionPresent = interactionMSgCount.find(
    (contact: any) => contact.interactionID === selectedCallContact?.id
  );
  const contactElevatedDetails = useSelector(getContactElevationDetails);
  const { agentReply } = interactionTabs[activeInteractionID];
  const { setCoBrowsingSessionClosed } = interactionSliceActions;
  const isCoBrowsingSessionStarted = useSelector(getCoBrowsingSessionStarted);
  const [emailSubject, setEmailSubject] = useState(
    selectedCallContact?.copilotAssist === 'Yes'
      ? 'Damaged Luggage Claim'
      : "RE: Where's my refund?"
  );

  const ageArray = [
    { id: '1', age: '18-24' },
    { id: '2', age: '25-34' },
    { id: '3', age: '35-44' },
  ];
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (
      selectedCallContact?.isElevated &&
      selectedCallContact?.channel === DigitalChannelType.CHAT &&
      selectedCallContact?.copilotAssist === 'Yes' &&
      selectedCallContact?.copilotSource === 'JSON'
    ) {
      setEmailSubject(
        contactElevatedDetails.get('email')?.emailTemplate?.subject
      );
    }
  }, [contactElevatedDetails]);

  
  /**
   * handle close event for pop up
   * @param event - any
   * @returns -
   * ```
   * @example
   * displayText(event)
   * ```
   */
  const displayText = {
    [InteractionTabType.SMS as string]: 'SMS',
    // [InteractionTabType.INBOUND_VOICE as string]: 'Inbound Voice',
    sms: 'SMS',
    tw: translate('X'),
    ig: 'Instagram',
    email: 'Email',
    fb: translate('fb'),
    chat: translate('chat'),
    li: 'Linkedin',
    VideoCall: translate('CRM'),
    reimbursementClaim: 'Reimbursement Claim',
  };

  /**
   * handle close event for pop up
   * @param event - any
   * @returns -
   * ```
   * @example
   * handleClick(event)
   * ```
   */
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * handle close event for pop up
   * ```
   * @example
   * handleClose()
   * ```
   */
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const [age, setAge] = useState(ageArray.length ? ageArray[0].age : '');

  /**
   * Component displays Caption for interaction Tab
   * @param tabName -string
   * @returns Caption for interaction Tab
   * ```
   * @example
   * callsetdetailsPaneCaption('sms')
   * ```
   */
  const callsetdetailsPaneCaption = (tabName: string) => {
    let returnVal = '';
    switch (tabName) {
      case 'SMS':
        returnVal = 'SMS';
        break;
      case DigitalChannelType.CHAT:
        returnVal = translate('privateChat');
        break;
      case DigitalChannelType.EMAIL:
        returnVal = emailSubject;
        break;
      case DigitalChannelType.TWITTER:
        return (
          <>
            <CcfTweetEyeIcon
              viewBox="-2 -4 24 24"
              className={styles.tweetEyeIcon}
            />
            <CcfTypography
              translationKey="publicTweet"
              className={styles.bookmarkLabels}
              // classes={{ root: classes.headerContentMenuItemTypography }}
            />
          </>
        );
      case DigitalChannelType.INSTAGRAM:
        return (
          <>
            <CcfTweetEyeIcon
              viewBox="-2 -4 24 24"
              className={styles.tweetEyeIcon}
            />
            <CcfTypography
              translationKey="publicComment"
              className={styles.bookmarkLabels}
            />
          </>
        );
        break;
      case DigitalChannelType.FACEBOOK:
        returnVal = 'Facebook';
        break;
      case DigitalChannelType.LINKEDIN:
        returnVal = 'LinkedIn';
        break;
    }
    return returnVal;
  };

  /**
   * method to set tab as active
   * @param tabName -string
   * @returns -
   * ```
   * @example
   * setActiveTab('sms')
   * ```
   */
  const setActiveTab = (tabName: string) => {
    dispatch(
      updateActiveInteraction({
        id: selectedCallContact?.id,
        interactionId: tabName,
      })
    );
    // dispatch(setInteractionHeaderTabActive({id: activeInt.interactionID, tab: tabName}));
  };

  // const activeTab = activeInt.activeTab;

  /**
   * method to check tab is active or not
   * @param tabName -string
   * @returns - boolean
   * ```
   * @example
   * isActiveTab('sms')
   * ```
   */
  const isActiveTab = (tabName: string) => tabName === activeInteractionID;

  const classes = useStyles();

  const dropdownOptions = {
    menuItems: [
      {
        items: [
          {
            label: translate('sendTranscript'),
            icon: (
              <CcfTranscriptIcon classes={{ root: styles.minimizePanelIcon }} />
            ),
            closeOnSelection: true,
          },
          {
            label: translate('openInNewBrowserWindow'),
            icon: (
              <CcfNewWindowIcon classes={{ root: styles.minimizePanelIcon }} />
            ),
            closeOnSelection: true,
          },
          {
            label: translate('showOrigin'),
            icon: (
              <CcfShowOriginIcon classes={{ root: styles.minimizePanelIcon }} />
            ),
            closeOnSelection: true,
          },
        ],
      },
    ],
  };

  /**
   * method to trigger click event of popover menuitem
   * @param item -PopOverMenuItem
   * @returns -
   * ```
   * @example
   * onPopOverItemSelection('Show Origin')
   * ```
   */
  const onPopOverItemSelection = (item: PopOverMenuItem) => () => {
    if (item.label === '') {
      console.log('Clicked Menuitem');
    }
  };

  /**
   * method to End Cobrowsing session
   * endSessionHandler()
   * @example
   */
  const endSessionHandler = () => {
    dispatch(setCoBrowsingSessionClosed(true));
    dispatch(setCoBrowsingSessionStarted(false));
  };

  /**
   * method to display tabs
   * @returns - React Component
   * ```
   * @example
   * renderTabs()
   * ```
   */
  const renderTabs = () => (
    <div className={styles.container}>
      <div className={styles.header}>
        {Object.keys(interactionTabs || {}).map((tabName) => {
          const skillName: string = interactionTabs
            ? interactionTabs[tabName].realExternalPlatformId
            : '';
          return (
            skillName !== '####' && (
              <div
                key={tabName}
                className={`${isActiveTab(tabName) ? styles.active : ''} ${
                  styles.tab
                }`}
                onClick={() => setActiveTab(tabName)}
                id={`agent_interactionWorkspace_headerTab_${displayText[skillName]}`}
              >
                {renderIcons(skillName)}
                <div className={styles.tabContent}>
                  {displayText[skillName]}
                </div>
                {interactionTabs &&
                  interactionTabs[tabName]?.isDispositionCompleted && (
                    <CcfApprovedIcon
                      viewBox="0 0 20 20"
                      className={styles.iconDispositionCompleted}
                    ></CcfApprovedIcon>
                  )}
              </div>
            )
          );
        })}

        <div className={`${styles.newtab}`}>
          <CcfAddChannelIcon classes={{ root: classes.svgRoot }} />
        </div>
      </div>
    </div>
  );

  /**
   * method to display Bookmark data for tab
   * @returns - React Component
   * ```
   * @example
   * renderBookmark()
   * ```
   */
  const renderBookmark = () => (
    <>
      <div className={styles.bookmark}>
        <div className={styles.bookmarkContent}>
          {(activeInteractionType === 'sms' ||
            activeInteractionType === 'chat') && (
            <div>
              {' '}
              <span> {getMessageCount.count} {translate('messages')} </span>
            </div>
          )}
          <div className={styles.interactionHeaderBodyText}>
            {activeInteractionType === 'Chat' && (
              <>
                <CcfChatBoxMessageCountIcon
                  style={{ fill: '#FFB000', marginRight: '5px' }}
                />
                <CcfTypography className={styles.msgCounteText}>
                  {`${agentReply?.length - 1} ${translate('messages')}`}
                </CcfTypography>
              </>
            )}
            {callsetdetailsPaneCaption(activeInteractionType)} | # 22073542
          </div>
          {activeInteractionType !== 'Email' ? (
            <>
              <div>
                <CcfButton
                  aria-controls="age-menu"
                  aria-haspopup="true"
                  onClick={handleClick}
                  classes={{ root: classes.buttonRoot }}
                >
                  {' '}
                  <CcfTypography
                    className={`${styles.chatInfo} ${styles.bookmarkLabels}`}
                  />{' '}
                  | {translate('age')}{' '}
                </CcfButton>{' '}
              </div>
              <Menu
                id="age-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                classes={{ paper: styles.menuPopupHeight }}
              >
                {ageArray.map((val) => (
                  <MenuItem
                    id={val.id}
                    classes={{
                      root: `${styles.menuItem} ${classes.listItemRoot}`,
                    }}
                    key={val.id}
                    onClick={() => {
                      setAge(val.age);
                      handleClose();
                    }}
                  >
                    <span className={styles.minimizePanelLabel}>
                      {val.age}
                      {val.age === age && (
                        <span className={styles.marginLeft20PX}>
                          <CcfApprovedIcon viewBox="0 0 30 30" />
                        </span>
                      )}
                    </span>
                  </MenuItem>
                ))}
              </Menu>
              <div>
                <CcfTypography
                  translationKey="timezone"
                  className={styles.bookmarkLabels}
                />{' '}
                | MST
              </div>
              <div style={{ display: 'none' }}>
                <CcfTypography
                  translationKey="companySize"
                  className={styles.bookmarkLabels}
                />{' '}
                | Enterprise
              </div>
              <div style={{ flexGrow: 1 }}></div>
            </>
          ) : (
            <>
              <div style={{ flexGrow: 1 }}></div>
              <div>
                <img src="./../assets/Ellipse_4720.png" alt="Elicipse" />
                <CcfTypography className={styles.bookmarkLabels}>
                  {'2 '}{translate('messages')}
                </CcfTypography>
              </div>
            </>
          )}

          <div>
            <button
              className={styles.viewButton}
              onClick={() => setViewDetails(!viewDetails)}
            >
              <CcfTypography
                translationKey={viewDetails ? 'hideDetails' : 'viewDetails'}
              />
              <CcfDoubleArrowIcon
                className={`${styles.doubleArrowIcon} ${
                  viewDetails ? styles.upArrow : ''
                }`}
              />
            </button>
          </div>
          <CcfPopOver
            onPopOverItemSelection={onPopOverItemSelection}
            optionList={dropdownOptions}
          />
        </div>
        {viewDetails && (
          <div className={styles.locationDetails}>
            <span className={styles.chatInfoLocation}>Location | Utah, US</span>
            <span className={styles.chatInfoLocation}>VIP | Yes</span>
          </div>
        )}
      </div>
      {isCoBrowsingSessionStarted && (
        <div className={styles.coBrowseSessionProgress}>
          <CcfBox style={{ display: 'flex', alignItems: 'center' }}>
            <CcfCoBrowseIcon className={styles.coBrowseIcon} />
            <CcfTypography className={styles.info}>
              {translate('coBrowseSessionInProgress')}
            </CcfTypography>
          </CcfBox>
          <CcfBox>
            <CcfButton
              className={styles.endSessionBtn}
              onClick={endSessionHandler}
            >
              <CcfTypography className={styles.endSessionBtnTxt}>
              {translate('endSession')}
              </CcfTypography>
            </CcfButton>
          </CcfBox>
        </div>
      )}
    </>
  );
  return (
    <CcfHeader
      HeaderWithTab={renderTabs()}
      HeaderWithBookmark={renderBookmark()}
      {...props}
    />
  );
}

export default CcfInteractionSpaceHeaderTab;
